/**
 * @fileoverview Utility functions for FontAwesome icon name conversions.
 *
 * These utilities help bridge the gap between kebab-case icon names used
 * in the component API (e.g., "chevron-left") and the PascalCase / camelCase
 * naming convention used by FontAwesome's JS icon packages (e.g., "faChevronLeft").
 */

import { FAAnimationProps } from './types/propsType';

// Helper
function kebabToPascalCase(kebab: string): string {
  return kebab
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

/**
 * Converts a kebab-case icon name to the FontAwesome JS import name.
 * Prepends the `fa` prefix and converts to PascalCase.
 *
 * @example
 *   toFAImportName('chevron-left')       // → 'faChevronLeft'
 *   toFAImportName('house')              // → 'faHouse'
 */
export function toFAImportName(iconName: string): string {
  return `fa${kebabToPascalCase(iconName)}`;
}

export function mapAnimationToFAProps(animationClassName: string): FAAnimationProps {
  if (!animationClassName) {
    return {};
  }

  const props: FAAnimationProps = {};

  // Handle compound classes (e.g. "fa-spin fa-spin-reverse", "fa-spinner-third fa-spin-pulse")
  const classes = animationClassName.trim().split(/\s+/);

  for (const c of classes) {
    switch (c) {
      case 'fa-beat':
        props.beat = true;
        break;
      case 'fa-beat-fade':
        props.beatFade = true;
        break;
      case 'fa-bounce':
        props.bounce = true;
        break;
      case 'fa-fade':
        props.fade = true;
        break;
      case 'fa-flip':
        props.flip = 'both';
        break;
      case 'fa-shake':
        props.shake = true;
        break;
      case 'fa-spin':
        props.spin = true;
        break;
      case 'fa-spin-reverse':
        props.spinReverse = true;
        break;
      case 'fa-spin-pulse':
        props.spinPulse = true;
        break;
      default:
        break;
    }
  }

  return props;
}
